/**
 * Normalizes date cells coming out of an uploaded Excel sheet to YYYY-MM-DD.
 *
 * Excel date cells arrive as serial numbers (e.g. 40278) or Date objects, and
 * users also type DD/MM/YYYY. Anything blank or unparseable returns '' so the
 * backend stores NULL instead of MySQL's '0000-00-00'.
 *
 * Mirrors `normalizeToMySQLDate` in nodefork
 * (src/utils/date/convert-date-format.util.ts) — keep the two in sync.
 */
export function normalizeDateCell(value: unknown): string {
  if (value === null || value === undefined) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  const build = (y: number, m: number, d: number): string => {
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return '';
    const probe = new Date(Date.UTC(y, m - 1, d));
    if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return '';
    return `${y}-${pad(m)}-${pad(d)}`;
  };

  // Excel serial date (days since 1899-12-30)
  const fromExcelSerial = (serial: number): string => {
    if (!Number.isFinite(serial) || serial < 1 || serial > 73415) return '';
    const dt = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000);
    return build(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  };

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '';
    return build(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value === 'number') return fromExcelSerial(value);

  const str = String(value).trim();
  if (!str) return '';
  if (/^0{4}-0{2}-0{2}/.test(str)) return '';

  if (/^\d+(\.\d+)?$/.test(str)) return fromExcelSerial(Number(str));

  const ymd = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T ].*)?$/);
  if (ymd) return build(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]));

  const dmy = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[T ].*)?$/);
  if (dmy) {
    const first = Number(dmy[1]);
    const second = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (first > 12 && second <= 12) return build(year, second, first);
    if (second > 12 && first <= 12) return build(year, first, second);
    return build(year, second, first); // default DD/MM/YYYY
  }

  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return build(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  return '';
}

/** True when the cell holds something the user typed but we could not read as a date. */
export function isUnparseableDate(value: unknown): boolean {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  if (!raw) return false;
  return normalizeDateCell(value) === '';
}
